import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import Toast from '@/common/components/toast/toast.component';
import { DomainConfigManager } from '@/common/utils/domain-config-manager';
import { manualLogout } from './auth-actions.client';
import { fetchAuthSession, getCachedAccessToken } from './auth-session.client';

// Debounced background session warm-up so we don't spam session resolution
let warmInFlight: Promise<any> | null = null;
let lastWarm = 0;
function scheduleAuthWarm() {
  const now = Date.now();
  if (warmInFlight || now - lastWarm < 5000) return; // at most once per 5s
  warmInFlight = fetchAuthSession(true)
    .catch(() => null)
    .finally(() => {
      lastWarm = Date.now();
      warmInFlight = null;
    });
}

const isBrowser = () => typeof window !== 'undefined';

function showErrorToast(message?: string) {
  if (isBrowser() && message) {
    Toast.error(message);
  }
}

interface ApiErrorData {
  error?: {
    details?: Array<{ message?: string }>;
    message?: string;
  };
}

function handleResponseError(error: any, disableToast: boolean) {
  if (!isBrowser()) return;

  if (error.response?.status === 401) {
    void manualLogout();
    return;
  }

  if (disableToast || error.response?.status < 400) return;

  const responseError = (error.response?.data as ApiErrorData | undefined)
    ?.error;
  if (Array.isArray(responseError?.details)) {
    responseError.details.forEach((errMsg) => {
      showErrorToast(errMsg.message);
    });
    return;
  }

  showErrorToast(responseError?.message);
}

class Client {
  readonly axiosInstance: AxiosInstance;
  private readonly disableToast: boolean | undefined;

  private async resolveBaseURL(): Promise<string> {
    return await DomainConfigManager.getBaseURL();
  }

  constructor(disableToast?: boolean) {
    this.disableToast = disableToast ?? false;
    this.axiosInstance = axios.create({
      timeout: 60000, // 60 seconds timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // You can modify the request config here (e.g., add headers, authentication tokens, etc.)
        const headers: any = {};
        if (isBrowser()) {
          // Fast path: only use already-cached token; avoid triggering extra session reads per request.
          const access = getCachedAccessToken();
          if (access) {
            headers.Authorization = `Bearer ${access}`;
          } else {
            // Throttled background warm-up to populate cache (non-blocking)
            scheduleAuthWarm();
          }
        }
        return { ...config, headers: { ...headers, ...config.headers } };
      },
      async (error) => {
        // Handle request error

        return await Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // You can modify the response here (e.g., handle common errors, transform data, etc.)
        return response;
      },

      async (error) => {
        handleResponseError(error, Boolean(this.disableToast));

        return await Promise.reject(error);
      }
    );
  }

  public async get<T>(
    url: string,
    params: object = {},
    headers: object = {}
  ): Promise<T> {
    try {
      const baseURL = await this.resolveBaseURL();
      const fullUrl = baseURL + url;
      const response = await this.axiosInstance.get<T>(fullUrl, {
        params,
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async post<T>(
    url: string,
    data: object = {},
    headers: object = {}
  ): Promise<T> {
    try {
      const baseURL = await this.resolveBaseURL();
      const fullUrl = baseURL + url;
      const response = await this.axiosInstance.post<T>(fullUrl, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async put<T>(
    url: string,
    data: object = {},
    headers: object = {}
  ): Promise<T> {
    try {
      const baseURL = await this.resolveBaseURL();
      const fullUrl = baseURL + url;
      const response = await this.axiosInstance.put<T>(fullUrl, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async patch<T>(
    url: string,
    data: object = {},
    headers: object = {}
  ): Promise<T> {
    try {
      const baseURL = await this.resolveBaseURL();
      const fullUrl = baseURL + url;
      const response = await this.axiosInstance.patch<T>(fullUrl, data, {
        headers,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async delete<T>(
    url: string,
    headers: object = {},
    data: object = {}
  ): Promise<T> {
    try {
      const baseURL = await this.resolveBaseURL();
      const fullUrl = baseURL + url;
      const response = await this.axiosInstance.delete<T>(fullUrl, {
        headers,
        data,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default Client;
